/**
 * Environment-specific URLs and third-party keys.
 * Override at build/dev time via Vite env vars (see `.env.example`).
 */

const {
  MODE,
  PROD,
  VITE_APP_ENV,
  VITE_API_BASE_URL,
  VITE_API_TIMEOUT_MS,
  VITE_API_LOCALE_HEADER,
  VITE_STRIPE_PUBLISHABLE_KEY,
  VITE_VENDOR_SIGNUP_URL,
  VITE_CONTACT_US_URL,
  VITE_CUSTOMER_SITE_URL,
  VITE_VENDOR_PORTAL_URL,
  VITE_AWS_REGION,
  VITE_S3_DEPLOY_BUCKET_TEST,
  VITE_AWS_PROFILE_TEST,
  VITE_S3_DEPLOY_BUCKET_PROD,
  VITE_AWS_PROFILE_PROD,
  VITE_GA_TRACKING_ID,
} = import.meta.env;

/** Axios requires http(s) scheme; bare host:port breaks CORS/XHR. */
function normalizeApiBaseUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  let normalized = url.trim();
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `http://${normalized.replace(/^\/+/, '')}`;
  }
  return normalized.replace(/\/+$/, '');
}

const defaults = {
  test: {
    apiBaseUrl:
      'https://ly4bn5hjaikxg5bk7itufmoxhy0dlhvn.lambda-url.eu-north-1.on.aws/vpm/rest/api/v1',
    stripePublishableKey:
      'pk_test_51H8phWHwvquor9705jOLSwtiW1C30Tkcx7umDyFWLmNhZ4I2bsJCaNc6QGNaZ3u8EGyQnBo8Abk9c4WUEhHkYj1100I7JiyA2g',
    vendorSignupUrl:
      'http://vendor.wedplanix.test.se.s3-website.eu-north-1.amazonaws.com/signup',
    contactUsUrl:
      'http://wedplanix.test.se.s3-website.eu-north-1.amazonaws.com/contact-us',
    customerSiteUrl:
      'http://wedplanix.test.se.s3-website.eu-north-1.amazonaws.com',
    vendorPortalUrl:
      'http://vendor.wedplanix.test.se.s3-website.eu-north-1.amazonaws.com',
  },
  prod: {
    apiBaseUrl: 'https://rest.wedplanix.se/vpm/rest/api/v1',
    stripePublishableKey:
      'pk_live_51H8phWHwvquor970Us0ctftza0jI7KeRYMPKi7eqwUkgdPXGD9WhNlAWTVom91CO1o7hk2KQEXGGSrwfTAXAlUae00TRdspOQm',
    vendorSignupUrl: 'https://vendor.wedplanix.se/signup',
    contactUsUrl: 'https://wedplanix.se/contact-us',
    customerSiteUrl: 'https://wedplanix.se',
    vendorPortalUrl: 'https://vendor.wedplanix.se',
  },
  local: {
    apiBaseUrl:
      'https://ly4bn5hjaikxg5bk7itufmoxhy0dlhvn.lambda-url.eu-north-1.on.aws/vpm/rest/api/v1',
    // 'http://localhost:8095/vpm/rest/api/v1',
    stripePublishableKey:
      'pk_test_51H8phWHwvquor9705jOLSwtiW1C30Tkcx7umDyFWLmNhZ4I2bsJCaNc6QGNaZ3u8EGyQnBo8Abk9c4WUEhHkYj1100I7JiyA2g',
    vendorSignupUrl:
      'http://vendor.wedplanix.test.se.s3-website.eu-north-1.amazonaws.com/signup',
    contactUsUrl: 'http://localhost:8000/contact-us',
    customerSiteUrl: 'http://localhost:8000',
    vendorPortalUrl:
      'http://vendor.wedplanix.test.se.s3-website.eu-north-1.amazonaws.com',
  },
};

function resolveProfile() {
  if (VITE_APP_ENV) {
    return VITE_APP_ENV;
  }
  /** Vite `--mode test` loads `.env.test` and sets MODE=test */
  if (MODE === 'test') {
    return 'test';
  }
  if (PROD) {
    return 'prod';
  }
  return 'local';
}

const activeProfile = resolveProfile();
const profileDefaults = defaults[activeProfile] || defaults.test;

export const appConfig = {
  /** Active profile name when not using explicit VITE_* overrides. */
  environment: activeProfile,

  /** Session via httpOnly SESSION cookie; see docs/frontend-api-security-handoff.md */
  auth: {
    mode: 'cookie',
    usesHttpOnlyCookie: true,
  },

  api: {
    baseUrl: normalizeApiBaseUrl(
      VITE_API_BASE_URL || profileDefaults.apiBaseUrl,
    ),
    timeoutMs: Number(VITE_API_TIMEOUT_MS) || 60000,
    defaultLocaleHeader: VITE_API_LOCALE_HEADER || 'SE',
  },

  stripe: {
    publishableKey:
      VITE_STRIPE_PUBLISHABLE_KEY || profileDefaults.stripePublishableKey,
  },

  urls: {
    vendorSignup: VITE_VENDOR_SIGNUP_URL || profileDefaults.vendorSignupUrl,
    contactUs: VITE_CONTACT_US_URL || profileDefaults.contactUsUrl,
    customerSite: VITE_CUSTOMER_SITE_URL || profileDefaults.customerSiteUrl,
    vendorPortal: VITE_VENDOR_PORTAL_URL || profileDefaults.vendorPortalUrl,
  },

  /** S3 / static hosting (deploy + website endpoints). */
  s3: {
    region: VITE_AWS_REGION || 'eu-north-1',
    deploy: {
      test: {
        bucket: VITE_S3_DEPLOY_BUCKET_TEST || 'wedplanix.test.se',
        profile: VITE_AWS_PROFILE_TEST || 'vpm-test',
      },
      prod: {
        bucket: VITE_S3_DEPLOY_BUCKET_PROD || 'www.wedplanix.se',
        profile: VITE_AWS_PROFILE_PROD || 'vpm-prod',
      },
    },
    websiteEndpoints: {
      customerTest:
        'http://wedplanix.test.se.s3-website.eu-north-1.amazonaws.com',
      vendorTest:
        'http://vendor.wedplanix.test.se.s3-website.eu-north-1.amazonaws.com',
    },
  },

  analytics: {
    googleTrackingId: VITE_GA_TRACKING_ID || 'UA-188025665-1',
  },
};

/** @deprecated Prefer `appConfig.api.baseUrl` */
export const vpmServerUrl = appConfig.api.baseUrl;

/** @deprecated Prefer `appConfig.stripe.publishableKey` */
export const stripePublishKey = appConfig.stripe.publishableKey;

/** @deprecated Prefer `appConfig.urls.vendorSignup` */
export const { vendorSignup } = appConfig.urls;

/** @deprecated Prefer `appConfig.urls.contactUs` */
export const contactUsUrl = appConfig.urls.contactUs;

/** @deprecated Prefer `appConfig.analytics.googleTrackingId` */
export const googleAnalytisTrackingId = appConfig.analytics.googleTrackingId;

export default appConfig;
